import { View, Text, ScrollView, ActivityIndicator, Pressable, Linking, Platform, RefreshControl } from 'react-native';
import React, { useCallback, useRef, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { getCachedOrgID } from '@/data/organization';
import { changeOrganizationPlan, createBillingPortalSession } from '@/data/billing';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg, isPaidOrg, isLegacyPricing, isSetupPending, isBillingRestricted } from '@/utils/organizationEntitlements';
import { showAlert } from '@/utils/showAlert';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import Callout from '@/components/General/Callout';
import CutCard from '@/components/General/CutCard';
import SolidButton from '@/components/General/SolidButton';
import Toast from 'react-native-toast-message';

function InfoRow({ label, value }) {
    return (
        <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>{label}</Text>
            <Text style={styles.infoValue}>{value || '—'}</Text>
        </View>
    );
}

function formatDate(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(amount, currency = 'usd') {
    const value = (amount ?? 0) / 100;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
    }).format(value);
}

function capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function invoiceStatusColor(status) {
    if (status === 'paid') return 'success';
    if (status === 'open') return 'warning';
    if (status === 'void' || status === 'uncollectible') return 'error';
    return 'textSecondary';
}

const ACCESS_STATE_LABELS = {
    free: 'Free',
    setup_pending: 'Setup Pending',
    paid: 'Active',
    billing_restricted: 'Billing Restricted',
};

// Stripe return URL must be an allowlisted http(s) origin — on native there is
// no app origin, so the hosted web app is the landing page after checkout.
const billingReturnURL = () => {
    const origin = Platform.OS === 'web'
        ? window.location.origin
        : (process.env.EXPO_PUBLIC_APP_WEB_URL || 'https://app.reachchms.com');
    return `${origin}/settings/organization/billing`;
};

function openInvoice(url) {
    if (!url) return;
    Linking.openURL(url).catch(() =>
        Toast.show({ type: 'error', text2: 'Could not open the invoice.' })
    );
}

export default function BillingInfoScreen() {
    const queryClient = useQueryClient();
    const router = useRouter();
    const { reach_setup: setupReturn } = useLocalSearchParams();
    const checkoutTab = useRef(null);
    const [awaitingActivation, setAwaitingActivation] = useState(setupReturn === 'complete');
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements, isPending: entitlementsPending, isFetching: entitlementsFetching } = useOrganizationEntitlements();

    // Plan management is org admin/owner only on every plan. View Billing
    // Info retains view-only access for paid orgs that granted it.
    const canManagePlan = !!(loggedInUser?.isAdmin || loggedInUser?.isAccountOwner
        || entitlements?.isAdmin || entitlements?.isAccountOwner);
    const canView = canManagePlan || !!loggedInUser?.can?.('View Billing Info');

    // get-billing-info resolves 'View Billing Info' via check_permission —
    // Free orgs have no such permission name, so only Paid orgs fetch it.
    // Member usage vs pricing band rides on this payload.
    const billingInfoEnabled = canView && isPaidOrg(entitlements);
    const { data: billingInfo, isLoading, isFetching: billingFetching, refetch: refetchBilling } = Query.billing.info({
        options: { enabled: billingInfoEnabled },
    });

    const invalidatePlanQueries = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.organizationEntitlements.current(getCachedOrgID()) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.billing.info() });
    }, [queryClient]);

    // Webhooks may finish after Stripe redirects. Poll briefly, never grant
    // access from a return URL; authoritative entitlements drive activation.
    useFocusEffect(useCallback(() => {
        invalidatePlanQueries();
        if (isPaidOrg(entitlements) || !(awaitingActivation || isSetupPending(entitlements))) return;
        let attempts = 0;
        const timer = setInterval(() => {
            invalidatePlanQueries();
            if (++attempts >= 40) clearInterval(timer);
        }, 3000);
        return () => clearInterval(timer);
    }, [invalidatePlanQueries, awaitingActivation, entitlements?.softwarePlan, entitlements?.accessState]));

    const prepareCheckoutTab = () => {
        // Reserve synchronously on click so popup blockers allow async checkout.
        if (Platform.OS === 'web') {
            checkoutTab.current = window.open('about:blank', '_blank');
            if (checkoutTab.current) checkoutTab.current.opener = null;
        }
    };
    const closeCheckoutTab = () => {
        checkoutTab.current?.close();
        checkoutTab.current = null;
    };
    const openExternalUrl = (url) => {
        if (Platform.OS === 'web') {
            if (checkoutTab.current && !checkoutTab.current.closed) {
                checkoutTab.current.location.replace(url);
                checkoutTab.current = null;
            } else {
                window.location.assign(url);
            }
        } else {
            router.push({ pathname: '/webview', params: { url, title: 'Plan & Billing', returnURL: billingReturnURL() } });
        }
    };

    const planMutation = useMutation({
        mutationFn: changeOrganizationPlan,
        onSuccess: (result) => {
            invalidatePlanQueries();
            if (result?.status === 'requires_setup' && result?.setupUrl) {
                setAwaitingActivation(true);
                openExternalUrl(result.setupUrl);
                Toast.show({ type: 'info', text2: 'Complete checkout to activate Reach Complete for this organization.' });
            } else if (result?.status === 'activated') {
                closeCheckoutTab();
                Toast.show({ type: 'success', text2: 'Reach Complete activated.' });
            } else if (result?.status === 'canceled_within_guarantee') {
                Toast.show({ type: 'success', text2: result?.note || 'Canceled — subscription charges waived.' });
            } else if (result?.status === 'scheduled') {
                Toast.show({ type: 'success', text2: result?.note || `Switching to Free on ${formatDate(result?.effectiveAt) || 'period end'}.` });
            } else if (result?.status === 'requires_setup') {
                closeCheckoutTab();
                // Pending attempt mid-activation — no checkout URL to resume.
                Toast.show({ type: 'info', text2: 'Setup is already finishing — check back in a moment.' });
            } else if (result?.status === 'setup_canceled') {
                Toast.show({ type: 'success', text2: 'Paid setup canceled.' });
            } else {
                Toast.show({ type: 'success', text2: 'Plan updated.' });
            }
        },
        onError: (error) => {
            closeCheckoutTab();
            Toast.show({ type: 'error', text2: error?.message || 'Could not change the plan.' });
        },
    });

    const portalMutation = useMutation({
        mutationFn: createBillingPortalSession,
        onSuccess: (result) => {
            if (result?.portalUrl) {
                openExternalUrl(result.portalUrl);
            } else {
                closeCheckoutTab();
                Toast.show({ type: 'error', text2: 'No billing portal is available for this organization.' });
            }
        },
        onError: (error) => {
            closeCheckoutTab();
            Toast.show({ type: 'error', text2: error?.message || 'Could not open the billing portal.' });
        },
    });

    if (!canView) {
        return (
            <View style={styles.centerContainer}>
                <Text style={styles.noAccessText}>You do not have permission to view plan and billing information.</Text>
            </View>
        );
    }

    const startPaidSetup = () => {
        prepareCheckoutTab();
        planMutation.mutate({ target: 'paid', returnURL: billingReturnURL() });
    };

    const confirmCancelSetup = () => {
        showAlert({
            title: 'Cancel paid setup?',
            subtitle: 'Your organization stays on the Free plan. You can restart setup anytime.',
            confirmText: 'Cancel Setup',
            isDestructive: true,
            onConfirm: () => planMutation.mutate({ target: 'free', confirmation: true, returnURL: billingReturnURL() }),
        });
    };

    const confirmSwitchToFree = () => {
        const guarantee = billingInfo?.guarantee;
        const legacyWarning = isLegacyPricing(entitlements)
            ? ' Your legacy beta pricing is permanently forfeited — rejoining uses current pricing.'
            : '';
        const periodEnd = formatDate(billingInfo?.subscription?.currentPeriodEnd || billingInfo?.transition?.effectiveAt);

        if (guarantee?.eligibleNow) {
            const deadline = formatDate(guarantee?.deadline);
            showAlert({
                title: 'Cancel subscription?',
                subtitle: `You are still inside your first-period guarantee${deadline ? ` (ends ${deadline})` : ''}. Canceling now waives all subscription charges; SMS usage and donation processing fees are billed separately.${legacyWarning}`,
                confirmText: 'Cancel Subscription',
                isDestructive: true,
                onConfirm: () => planMutation.mutate({ target: 'free', confirmation: true, returnURL: billingReturnURL() }),
            });
            return;
        }

        showAlert({
            title: 'Switch to Free plan?',
            subtitle: `Your organization moves to the Free plan at the end of the current billing period${periodEnd ? ` (${periodEnd})` : ''}. Paid features stay active until then and the final period still bills normally.${legacyWarning}`,
            confirmText: 'Switch to Free',
            isDestructive: true,
            onConfirm: () => planMutation.mutate({ target: 'free', confirmation: true, returnURL: billingReturnURL() }),
        });
    };

    const openPortal = () => {
        prepareCheckoutTab();
        portalMutation.mutate({ returnURL: billingReturnURL() });
    };

    const onRefresh = () => {
        invalidatePlanQueries();
        if (billingInfoEnabled) refetchBilling();
    };

    const free = isFreeOrg(entitlements);
    const paid = isPaidOrg(entitlements);
    const setupPending = isSetupPending(entitlements);
    const restricted = isBillingRestricted(entitlements);
    const usage = billingInfo?.userUsage;
    const transition = billingInfo?.transition;
    const guarantee = billingInfo?.guarantee;
    const mutating = planMutation.isPending || portalMutation.isPending;

    const planLabel = !entitlements?.member
        ? '—'
        : free ? 'Free' : isLegacyPricing(entitlements) ? 'Complete · Legacy Beta' : 'Complete';

    const planChipVariant = restricted ? 'error' : paid ? 'success' : setupPending ? 'warning' : 'info';

    if (isLoading && billingInfoEnabled) {
        return (
            <View style={styles.centerContainer}>
                <ActivityIndicator size="large" />
            </View>
        );
    }

    const customer = billingInfo?.customer;
    const subscription = billingInfo?.subscription;
    const upcomingInvoice = billingInfo?.upcomingInvoice;
    const invoices = billingInfo?.invoices || [];

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.contentContainer}
            refreshControl={
                <RefreshControl
                    refreshing={entitlementsFetching || billingFetching}
                    onRefresh={onRefresh}
                />
            }
        >
            <View style={styles.header}>
                <View style={styles.headerIconContainer}>
                    <Unicon name="receipt-outline" colorVariant="onPrimary" size={24} />
                </View>
                <Text style={styles.title}>Plan & Billing</Text>
                <Text style={styles.description}>
                    Manage your organization&apos;s plan, billing, and invoice history.
                </Text>
            </View>

            {restricted && (
                <View testID="billing-restricted-banner">
                    <Callout
                        type="error"
                        title="Payment past due"
                        message="A payment failed and the 7-day grace period has expired. Paid features are restricted until billing is updated."
                    />
                    <SolidButton
                        text="Update Payment Method"
                        testID="update-payment-button"
                        iconName="card-outline"
                        onPress={openPortal}
                        processing={portalMutation.isPending}
                        disabled={mutating}
                    />
                </View>
            )}

            {setupPending && (
                <View testID="setup-pending-banner">
                    <Callout
                        type="warning"
                        title="Paid setup not finished"
                        message="Paid plan setup was started but checkout is not complete. Resume setup to activate paid features."
                    />
                </View>
            )}

            {transition?.target === 'free' && (
                <View testID="scheduled-transition-banner">
                    <Callout
                        type="info"
                        title="Switching to Free"
                        message={`Your plan is scheduled to change to Free${transition?.effectiveAt ? ` on ${formatDate(transition.effectiveAt)}` : ' at period end'}. Open Manage Billing to keep the Paid plan.`}
                    />
                </View>
            )}

            {/* Plan */}
            <CutCard style={styles.card} contentStyle={styles.cardContent} testID="plan-card">
                <View style={styles.cardHeader}>
                    <Unicon name="card-outline" size={20} colorVariant="primary" />
                    <Text style={styles.cardTitle}>Current Plan</Text>
                </View>
                <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Plan</Text>
                    <Chip colorVariant={planChipVariant} testID="plan-chip">
                        {entitlementsPending ? '…' : planLabel}
                    </Chip>
                </View>
                <InfoRow label="Status" value={ACCESS_STATE_LABELS[entitlements?.accessState] || '—'} />
                {usage?.currentCount != null && (
                    <InfoRow label="Registered Users" value={String(usage.currentCount)} />
                )}
                {usage?.period?.peakCount != null && (
                    <InfoRow label="Peak Users This Period" value={String(usage.period.peakCount)} />
                )}
                {usage?.band && (
                    <InfoRow label="Pricing Band" value={String(usage.band)} />
                )}
                {usage?.includedUsers != null && (
                    <InfoRow
                        label="Included Users"
                        value={usage.overageUsers > 0
                            ? `${usage.includedUsers} (+${usage.overageUsers} overage)`
                            : String(usage.includedUsers)}
                    />
                )}
                {usage?.totalCents > 0 && (
                    <InfoRow label="This Period (est.)" value={formatCurrency(usage.totalCents, usage.currency)} />
                )}
                {paid && guarantee?.eligibleNow && (
                    <Text style={styles.guaranteeText}>
                        First-period guarantee active{guarantee?.deadline ? ` until ${formatDate(guarantee.deadline)}` : ''}
                    </Text>
                )}

                {canManagePlan && (free || setupPending) && (
                    <View style={styles.actionRow}>
                        {free && (
                            <SolidButton
                                text="Upgrade to Complete"
                                testID="upgrade-to-paid-button"
                                iconName="arrow-up-circle-outline"
                                onPress={startPaidSetup}
                                processing={planMutation.isPending}
                                disabled={mutating}
                            />
                        )}
                        {setupPending && (
                            <>
                                <SolidButton
                                    text="Resume Complete Setup"
                                    testID="resume-setup-button"
                                    iconName="arrow-forward-circle-outline"
                                    onPress={startPaidSetup}
                                    processing={planMutation.isPending}
                                    disabled={mutating}
                                />
                                <SolidButton
                                    text="Cancel Setup"
                                    testID="cancel-setup-button"
                                    variant="outlined"
                                    type="textSecondary"
                                    onPress={confirmCancelSetup}
                                    disabled={mutating}
                                />
                            </>
                        )}
                    </View>
                )}

                {canManagePlan && paid && (
                    <View style={styles.actionRow}>
                        <SolidButton
                            text="Manage Billing"
                            testID="manage-billing-button"
                            iconName="card-outline"
                            onPress={openPortal}
                            processing={portalMutation.isPending}
                            disabled={mutating}
                        />
                        {!transition && (
                            <SolidButton
                                text="Cancel Subscription"
                                testID="switch-to-free-button"
                                variant="outlined"
                                type="error"
                                onPress={confirmSwitchToFree}
                                disabled={mutating}
                            />
                        )}
                    </View>
                )}

                {free && (
                    <Text style={styles.upgradeHint}>
                        Paid unlocks events, small groups, tasks, messaging, forms, and more.
                    </Text>
                )}
            </CutCard>

            {/* Upcoming Bill */}
            {upcomingInvoice && (
                <CutCard style={styles.card} contentStyle={styles.cardContent}>
                    <View style={styles.cardHeader}>
                        <Unicon name="document-text-outline" size={20} colorVariant="primary" />
                        <Text style={styles.cardTitle}>Upcoming Bill</Text>
                    </View>
                    <InfoRow label="Next Billing Date" value={formatDate(upcomingInvoice.nextBillingDate)} />
                    {subscription?.currentPeriodStart && subscription?.currentPeriodEnd && (
                        <InfoRow
                            label="Billing Period"
                            value={`${formatDate(subscription.currentPeriodStart)} – ${formatDate(subscription.currentPeriodEnd)}`}
                        />
                    )}
                    {upcomingInvoice.lineItems?.filter((item) => item.amount !== 0).map((item, i) => (
                        <View key={i} style={styles.infoRow}>
                            <Text style={styles.infoLabel} numberOfLines={2}>{item.description || '—'}</Text>
                            <Text style={styles.infoValue}>{formatCurrency(item.amount, upcomingInvoice.currency)}</Text>
                        </View>
                    ))}
                    <View style={styles.costRow}>
                        <Text style={styles.costLabel}>Estimated Total</Text>
                        <Text style={styles.costValue}>{formatCurrency(upcomingInvoice.total, upcomingInvoice.currency)}</Text>
                    </View>
                </CutCard>
            )}

            {/* Invoice History */}
            {invoices.length > 0 && (
                <CutCard style={styles.card} contentStyle={styles.cardContent}>
                    <View style={styles.cardHeader}>
                        <Unicon name="receipt-outline" size={20} colorVariant="primary" />
                        <Text style={styles.cardTitle}>Invoice History</Text>
                    </View>
                    {invoices.map((invoice) => (
                        <Pressable
                            key={invoice.id}
                            testID={`invoice-${invoice.id}`}
                            style={styles.invoiceRow}
                            onPress={() => openInvoice(invoice.pdfUrl)}
                            disabled={!invoice.pdfUrl}
                        >
                            <View style={styles.invoiceInfo}>
                                <Text style={styles.invoiceDate}>{formatDate(invoice.date)}</Text>
                                <Text style={styles.invoiceNumber}>{invoice.number || invoice.id}</Text>
                            </View>
                            <View style={styles.invoiceRight}>
                                <Text style={styles.invoiceAmount}>{formatCurrency(invoice.total, invoice.currency)}</Text>
                                <Chip colorVariant={invoiceStatusColor(invoice.status)}>
                                    {capitalize(invoice.status)}
                                </Chip>
                                {invoice.pdfUrl && (
                                    <Unicon name="open-outline" size={14} colorVariant="textSecondary" />
                                )}
                            </View>
                        </Pressable>
                    ))}
                </CutCard>
            )}

            {/* Customer Info */}
            {customer && (
                <CutCard style={styles.card} contentStyle={styles.cardContent}>
                    <View style={styles.cardHeader}>
                        <Unicon name="person-outline" size={20} colorVariant="primary" />
                        <Text style={styles.cardTitle}>Account Owner</Text>
                    </View>
                    <InfoRow label="Name" value={customer.name} />
                    <InfoRow label="Email" value={customer.email} />
                    <InfoRow label="Phone" value={customer.phone} />
                    {customer.address && (
                        <InfoRow
                            label="Address"
                            value={[
                                customer.address.line1,
                                customer.address.line2,
                                customer.address.city,
                                customer.address.state,
                                customer.address.postalCode,
                                customer.address.country,
                            ].filter(Boolean).join(', ')}
                        />
                    )}
                </CutCard>
            )}
        </ScrollView>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    contentContainer: {
        padding: spacing.md,
        gap: spacing.md,
        _web: {
            maxWidth: 800,
            alignSelf: 'center',
            width: '100%',
        },
    },
    centerContainer: {
        flex: 1,
        backgroundColor: colors.background,
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.lg,
    },
    noAccessText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    header: {
        alignItems: 'center',
        paddingVertical: spacing.sm,
        gap: spacing.xs,
    },
    headerIconContainer: {
        width: 48,
        height: 48,
        borderRadius: 14,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: spacing.xs,
    },
    title: {
        fontSize: typography.h1,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    description: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        lineHeight: 22,
        maxWidth: 500,
    },
    card: {
        borderRadius: 12,
        boxShadow: shadows.card,
    },
    cardContent: {
        padding: spacing.md,
        gap: spacing.sm,
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        marginBottom: spacing.xs,
    },
    cardTitle: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    infoLabel: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        flexShrink: 1,
        marginRight: spacing.sm,
    },
    infoValue: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.text,
        textAlign: 'right',
        flexShrink: 1,
    },
    guaranteeText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.success,
        lineHeight: 20,
    },
    actionRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.sm,
        marginTop: spacing.xs,
    },
    upgradeHint: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        lineHeight: 20,
        marginTop: spacing.xs,
    },
    costRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    costLabel: {
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    costValue: {
        fontSize: typography.h2,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    invoiceRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: spacing.xs,
        _web: {
            cursor: 'pointer',
        },
    },
    invoiceInfo: {
        flexShrink: 1,
        marginRight: spacing.sm,
    },
    invoiceDate: {
        fontSize: typography.body,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    invoiceNumber: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    invoiceRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    invoiceAmount: {
        fontSize: typography.body,
        fontFamily: fonts.bold,
        color: colors.text,
    },
}));
